import { prop } from "@suse-o11y/prop";
import { Tooltip, TooltipBoundary, TooltipTrigger } from "@suse-o11y/tooltip";
import React from "react";
import { useDispatch, useSelector } from "react-redux";

import { Star } from "~/components/star";
import { type NodeId, Permission } from "~/models/dto";

import { selectPlayground } from "../../../app/state/config/ConfigSelectors";
import { playgroundModal } from "../../../app/state/pages/WelcomeSlice";
import { usePermissions } from "../../../common/use-permissions";
import { useUserProfile } from "../../../common/use-user-profile";
import { VIEWS_OVERVIEW_PATH } from "../../../routes/Routes";
import { LinkTo } from "../link";

export interface ViewStarProps {
	viewId: NodeId;
	className?: string;
}

export const ViewStar: React.FC<ViewStarProps> = ({ viewId, ...props }) => {
	const permissions = usePermissions();
	const [starredViews, updateUserProfile] = useUserProfile({
		select: prop("starredViews"),
	});

	const isStarred = starredViews?.has(viewId);
	const isPlayground = useSelector(selectPlayground);

	if (isStarred && permissions?.hasSystemPermission(Permission.DeleteFavoriteViews)) {
		return (
			<TooltipBoundary>
				<TooltipTrigger asChild>
					<Star
						{...props}
						onStarredChange={() => {
							if (!isPlayground) {
								updateUserProfile((userProfile) => ({
									...userProfile,
									starredViews: userProfile.starredViews.delete(viewId),
								}));
							}
						}}
						starred
					/>
				</TooltipTrigger>

				<ViewStarTooltip isPlayground={isPlayground} prefix="Remove from " />
			</TooltipBoundary>
		);
	}

	if (!isStarred && permissions?.hasSystemPermission(Permission.CreateFavoriteViews)) {
		return (
			<TooltipBoundary>
				<TooltipTrigger asChild>
					<Star
						{...props}
						disabled={isStarred == null}
						onStarredChange={() => {
							if (!isPlayground) {
								updateUserProfile((userProfile) => ({
									...userProfile,
									starredViews: userProfile.starredViews.add(viewId),
								}));
							}
						}}
						starred={false}
					/>
				</TooltipTrigger>

				<ViewStarTooltip isPlayground={isPlayground} prefix="Add to " />
			</TooltipBoundary>
		);
	}

	return null;
};

const ViewStarTooltip: React.FC<{
	prefix: string;
	isPlayground: boolean;
}> = ({ prefix, isPlayground }) => {
	const dispatch = useDispatch();

	return (
		<Tooltip className="tooltip/tooltip select-none">
			{isPlayground ? (
				<>
					Available only in the trial.{" "}
					<a
						className="text-white underline visited:text-white hover:text-white hover:no-underline"
						onClick={() => {
							// TODO: STAC-16306 add event (When a tooltip Learn more link or an integration panel is clicked, send a button click event to Google Analytics that encapsulated which button is clicked.)
							dispatch(playgroundModal({ open: true }));
						}}
						role="link"
					>
						Learn more
					</a>
				</>
			) : (
				<>
					{prefix}
					<LinkTo
						className="text-white underline visited:text-white hover:text-white hover:no-underline"
						onClick={(event) => event.stopPropagation()}
						search={({ timeRange, timestamp, detachedFilters }) => ({
							timeRange,
							timestamp,
							detachedFilters,
							viewFilter: "starred",
						})}
						to={VIEWS_OVERVIEW_PATH}
					>
						starred views
					</LinkTo>
				</>
			)}
		</Tooltip>
	);
};
